import { action } from '@ember/object';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type { CentralMappingCategory } from 'lumen/models';

type DropdownMappingOption = Option & { id: string; label: string };

interface SettingsCentralMappingDropdownsDropdownCategoryFilterSignature {
  Args: {
    appliedCategoryIds: string[];
    hierarchyCategories?: CentralMappingCategory[];
    onChange: (categoryIds: string[]) => void;
  };
}

export default class SettingsCentralMappingDropdownsDropdownCategoryFilter extends Component<SettingsCentralMappingDropdownsDropdownCategoryFilterSignature> {
  @tracked appliedCategoriesOptions: DropdownMappingOption[] = [];

  get appliedCategoriesValue(): DropdownMappingOption[] {
    const { appliedCategoryIds } = this.args;
    return this.categoryOptions.filter((categoryOption) =>
      appliedCategoryIds.includes(categoryOption.id),
    );
  }

  @cached
  get categoryOptions(): DropdownMappingOption[] {
    if (!this.args.hierarchyCategories) {
      return [];
    }

    const primaryCategoryOrder = this.args.hierarchyCategories
      .filter((c) => c.depth === 0)
      .map((c) => c.name);

    const mappingOptions = this.args.hierarchyCategories
      .map((category) => {
        const breadcrumbs = category.path.map((cat) => cat.name).join(' > ');

        return { id: category.id || '', label: breadcrumbs };
      })
      .filter((option) => option.label);

    return this.sortDataByPriorityAndLabel(
      mappingOptions,
      primaryCategoryOrder,
    );
  }

  /**
   * Sorts data based on a predefined order of primary categories and then alphabetically by full label.
   * @param data - Array of objects containing a `label` property in the format 'Primary > Subcategory'.
   * @param order - Predefined order of primary categories.
   * @returns Sorted array of objects.
   * @throws Error if label format is invalid or if a category is not found in the predefined order.
   */
  sortDataByPriorityAndLabel(
    data: { id: string; label: string }[],
    order: string[],
  ): { id: string; label: string }[] {
    // Map primary category to its desired sort index
    const orderMap = new Map<string, number>();
    order.forEach((category, index) => {
      orderMap.set(category, index);
    });

    return data.sort((a, b) => {
      const labelA = a.label;
      const labelB = b.label;

      // Get the primary category (the part before the first ' > ')
      const primaryCategoryA = labelA.split(' > ')[0];
      const primaryCategoryB = labelB.split(' > ')[0];

      if (!primaryCategoryA || !primaryCategoryB) {
        throw new Error(
          `Invalid label format. Expected 'Primary > Subcategory'. Got '${labelA}' and '${labelB}'`,
        );
      }

      // Get the predefined index for comparison
      const indexA = orderMap.get(primaryCategoryA);
      const indexB = orderMap.get(primaryCategoryB);

      // 1. Primary Category Comparison
      // If indices are different, sort by the predefined order.
      if (indexA !== indexB) {
        if (indexA === undefined || indexB === undefined) {
          throw new Error(
            `Category not found in predefined order. Got '${primaryCategoryA}' and '${primaryCategoryB}'`,
          );
        }
        // If a category is not in the predefined order, treat it as a lower priority (or handle error)
        // Assuming all categories are in the predefined order, we use the difference of indices.
        return indexA - indexB;
      }

      // 2. Subcategory (Full Label) Comparison
      // If primary categories are the same, sort alphabetically by the full label.
      // This ensures 'Transient' comes before 'Transient > Individuals'
      // and 'Transient > Individuals' before 'Transient > Long Stay'.
      if (labelA < labelB) {
        return -1;
      }
      if (labelA > labelB) {
        return 1;
      }
      return 0; // labels are equal
    });
  }

  @action handleCategoryChange(options: DropdownMappingOption[]): void {
    this.args.onChange(options.map((category) => category.id));
  }

  <template>
    <PrismComboSelectInput
      @isSearchable={{true}}
      @onChange={{this.handleCategoryChange}}
      @options={{this.categoryOptions}}
      @unsafe_isMultipleImmediate={{true}}
      @unsafe_renderInPlace={{false}}
      @value={{this.appliedCategoriesValue}}
      data-test-id="dropdown-category-value"
    />
  </template>
}
